// BaseMapDataOverlaysTab.vue
<template>
  <div>
    <!-- ───────────────────────── DATA OVERLAYS ───────────────────────── -->
    <div class="row">
      <div class="col-12 px-0 pt-3 py-2">
        <p class="px-3 mb-0"><small><strong>Data overlays</strong></small></p>
        <div ref="overlayScroll" class="mapstyle-list">

          <div class="position-relative d-inline-block" @click="toggleSlopeOverlay">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': slopeAngleActive }">
              <img src="../../../images/map_style/slope_overlay_style.jpg" alt="Map" draggable="false" />
              <div class="hiking-text p-1"><p class="m-0"><small><strong>Slope Angle</strong></small></p></div>
            </div>
          </div>

          <div class="position-relative d-inline-block" @click="toggleCellCoverageOverlay">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': cellCoverageActive }">
              <img src="../../../images/map_style/cell_overlay_style.jpg" alt="Map" draggable="false" />
              <div class="hiking-text p-1"><p class="m-0"><small><strong>Cell coverage</strong></small></p></div>
            </div>
          </div>

          <div class="position-relative d-inline-block" @click="toggleHiikerTrailsOverlay">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': hiikerTrailsActive }">
              <img src="../../../images/map_style/trails_overlay_style.jpg" alt="Map" draggable="false" />
              <div class="hiking-text p-1"><p class="m-0"><small><strong>HiiKER trails</strong></small></p></div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- ───────────────────────── WEATHER MAPS ───────────────────────── -->
    <div class="row">
      <div class="col-12 px-0 py-1">
        <p class="px-3 mb-0"><small><strong>Weather maps</strong></small></p>
        <div ref="weatherScroll" class="mapstyle-list">

          <div class="position-relative d-inline-block" @click="toggleWeatherOverlay({type:'TA2',name:'temperature'})">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': activeWeatherLayer.name === 'temperature' }">
              <img src="../../../images/map_style/temperature_weather_style.jpg" alt="Temperature Map" draggable="false" />
              <div class="hiking-text p-1"><p class="m-0"><small><strong>Temperature</strong></small></p></div>
            </div>
          </div>

          <div class="position-relative d-inline-block" @click="toggleWeatherOverlay({type:'CL',name:'cloud'})">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': activeWeatherLayer.name === 'cloud' }">
              <img src="../../../images/map_style/cloud_weather_style.jpg" alt="Cloud Map" draggable="false" />
              <div class="hiking-text p-1"><p class="m-0"><small><strong>Cloud</strong></small></p></div>
            </div>
          </div>

          <div class="position-relative d-inline-block" @click="toggleWeatherOverlay({type:'PAR0',name:'rainfall'})">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': activeWeatherLayer.name === 'rainfall' }">
              <img src="../../../images/map_style/rainfall_weather_style.jpg" alt="Rainfall Map" draggable="false" />
              <div class="hiking-text p-1"><p class="m-0"><small><strong>Rainfall</strong></small></p></div>
            </div>
          </div>

          <div class="position-relative d-inline-block" @click="toggleWeatherOverlay({type:'PAS0',name:'snowfall'})">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': activeWeatherLayer.name === 'snowfall' }">
              <img src="../../../images/map_style/snowfall_weather_style.jpg" alt="Snowfall Map" draggable="false" />
              <div class="hiking-text p-1"><p class="m-0"><small><strong>Snowfall</strong></small></p></div>
            </div>
          </div>

          <div class="position-relative d-inline-block" @click="toggleWeatherOverlay({type:'WND',name:'wind'})">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': activeWeatherLayer.name === 'wind' }">
              <img src="../../../images/map_style/wind_weather_style.jpg" alt="Wind Map" draggable="false" />
              <div class="hiking-text p-1"><p class="m-0"><small><strong>Wind</strong></small></p></div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- ───────────────────────── MY TRAILS ───────────────────────── -->
    <div class="row">
      <div class="col-12 px-0 py-1">
        <p class="px-3 mb-0"><small><strong>My Trails</strong></small></p>
        <div ref="trailsScroll" class="mapstyle-list">

          <div
            v-for="(label, source) in trailOptions"
            :key="source"
            class="position-relative d-inline-block"
            @click="toggleTrail(source)"
          >
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': isSourceActive(source) }">
              <img :src="getTrailImage(source)" alt="Map" draggable="false" />
              <div class="hiking-text p-1"><p class="m-0"><small><strong>{{ label }}</strong></small></p></div>
            </div>
          </div>

        </div>
      </div>
    </div>

  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick } from 'vue';
import { mapState, mapGetters, mapActions } from 'vuex';

// Images that are strictly presentation-layer resources stay here
import plannedImage     from '../../../images/map_style/planned_vector_style.jpg';
import recordingsImage  from '../../../images/map_style/recordings_vector_style.jpg';
import completedImage   from '../../../images/map_style/completed_vector_style.jpg';
import emitter from "../../../utils/bus";

export default defineComponent({
  name: 'TrailOptions',

  computed: {
    ...mapState('mapDataOverlays', [
      /* presentational */
      'trailOptions',
      /* overlay flags – now owned by the store */
      'hiikerTrailsActive',
      'cellCoverageActive',
      'slopeAngleActive',
      'activeWeatherLayer'
    ]),
    ...mapGetters('mapDataOverlays', ['isSourceActive'])
  },

  watch: {
    /* ─────────── store actions ─────────── */
    hiikerTrailsActive: function (newVal: boolean) {
      console.log("HiiKER trails active: ", newVal);
    },
    cellCoverageActive: function (newVal: boolean) {
      console.log("Cell coverage active: ", newVal);
    },
    slopeAngleActive: function (newVal: boolean) {
      console.log("Slope angle active: ", newVal);
    },
    activeWeatherLayer: function (newVal: any) {
      console.log("Active weather layer: ", newVal);
    }
  },

  methods: {
    /* ─────────── store actions ─────────── */
    ...mapActions('mapDataOverlays', [
      'toggleTrail',
      'toggleHiikerTrailsOverlay',
      'toggleCellCoverageOverlay',
      'toggleSlopeOverlay',
      'toggleWeatherOverlay',
      'initOverlayListeners',
      'initActiveSources',
      'destroyOverlayListeners'
    ]),

    /* ─────────── purely UI helpers ─────────── */
    getTrailImage(source: string) {
      switch (source) {
        case 'get_user_recordings':       return recordingsImage;
        case 'get_user_planned_trails':   return plannedImage;
        case 'get_user_completed_trails': return completedImage;
        default:                          return plannedImage;
      }
    },

    makeDraggable(el: HTMLElement) {
      /* unchanged – provides swipe-scroll for the card lists */
      let isDown = false, startX = 0, scrollLeft = 0, moved = false;
      el.addEventListener('dragstart', e => e.preventDefault());
      el.addEventListener('mousedown',  e => { isDown = true; moved = false; el.classList.add('dragging');
        startX = e.pageX - el.offsetLeft; scrollLeft = el.scrollLeft; });
      el.addEventListener('mousemove',  e => { if (!isDown) return;
        const x = e.pageX - el.offsetLeft;
        if (Math.abs(x - startX) > 5) moved = true;
        el.scrollLeft = scrollLeft - (x - startX) * 1.5; });
      ['mouseup','mouseleave'].forEach(evt => el.addEventListener(evt, () => { isDown = false; el.classList.remove('dragging'); }));
      el.addEventListener('click', e => { if (moved) e.stopImmediatePropagation(); }, true);
    }
  },

  mounted() {
    this.initOverlayListeners();     // subscribe to bus + restore persisted overlays
    nextTick(() => {
      [this.$refs.overlayScroll, this.$refs.weatherScroll, this.$refs.trailsScroll]
        .forEach((el: any) => el && this.makeDraggable(el));
    });
  },

  beforeUnmount() {
    this.destroyOverlayListeners();  // unsubscribe + clean map layers
  }
});
</script>

<style scoped>
/* ───── (identical presentation styles – no logic here) ───── */
.mapstyle-list{display:flex;overflow-x:auto;max-width:100%;padding-bottom:1em;}
.mapstyle-list::-webkit-scrollbar{display:none}
.mapstyle-list>.position-relative{flex:0 0 auto;}
.mapstyle-card{position:relative;width:120px;background:#fff;border:2px solid #434343;border-radius:6px;cursor:pointer;margin-top:1em;margin-right:1em;transition:border-color .3s ease;overflow:hidden}
.mapstyle-card-active{border-color:#fddf00}
.mapstyle-card img{width:100%}
.mapstyle-card .hiking-text{position:absolute;left:0;bottom:0;width:100%;background:rgba(0,0,0,.7);color:#fff}
.mapstyle-list > .position-relative:first-child {
  margin-left: 1.5em;
}
@media(max-width:475px){.mapstyle-card{width:108px;margin-right:.7em}}
</style>
